import type { AxiosProgressEvent } from 'axios';
import { api } from './api';

export interface UserPrivateDocumentDto {
  id: string;
  fileName: string;
  fileType?: string | null;
  fileSize: number;
  processingStatus: string;
  indexedForChatbot: boolean;
  createdAt: string;
  updatedAt: string;
  workspaceChatMessageId?: string | null;
}

export interface MyPrivateStorageUsageDto {
  quotaConfigured: boolean;
  totalFiles: number;
  totalSizeBytes: number;
  maxStoragePerUserBytes: number;
  globalMaxStorageBytes?: number | null;
  percentUsed: number;
  totalTenantUsedBytes: number;
}

export interface UserPrivateDocumentDownloadUrlDto {
  url: string;
  expiresInSeconds: number;
}

export interface DocumentPublishRequestCreatedDto {
  id: string;
  status: string;
  userPrivateDocumentId: string;
  targetDocumentId?: string | null;
  notes?: string | null;
  createdAt: string;
}

export interface CreateUserDocumentPublishRequestBody {
  targetDocumentId?: string | null;
  notes?: string | null;
}

class UserPrivateDocumentService {
  async list(): Promise<UserPrivateDocumentDto[]> {
    const response = await api.get<UserPrivateDocumentDto[]>('/user-documents');
    return response.data ?? [];
  }

  async myUsage(): Promise<MyPrivateStorageUsageDto> {
    const response = await api.get<MyPrivateStorageUsageDto>('/user-documents/my-usage');
    return response.data;
  }

  async upload(
    file: File,
    onProgress?: (percent: number) => void,
    threadId?: string | null
  ): Promise<UserPrivateDocumentDto> {
    const form = new FormData();
    form.append('file', file);
    if (threadId) {
      form.append('threadId', threadId);
    }
    const response = await api.post<UserPrivateDocumentDto>('/user-documents/upload', form, {
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (!onProgress || !e.total) return;
        const pct = Math.round((e.loaded * 100) / e.total);
        onProgress(pct);
      },
    });
    return response.data;
  }

  async getDownloadUrl(id: string): Promise<UserPrivateDocumentDownloadUrlDto> {
    const response = await api.get<UserPrivateDocumentDownloadUrlDto>(`/user-documents/${id}/download`);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/user-documents/${id}`);
  }

  async createPublishRequest(
    documentId: string,
    body?: CreateUserDocumentPublishRequestBody
  ): Promise<DocumentPublishRequestCreatedDto> {
    const response = await api.post<DocumentPublishRequestCreatedDto>(
      `/user-documents/${documentId}/publish-request`,
      body ?? {}
    );
    return response.data;
  }
}

export const userPrivateDocumentService = new UserPrivateDocumentService();
